import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { Home, Calendar, UsersIcon, Search, Tickets } from 'lucide-react';
import { memo, useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { guardNavigate } from '@/lib/navigationGuard';

const containerVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.3 } },
};

function useScrollProgress(resetKey: string, threshold = 6) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
  }, [resetKey]);

  useEffect(() => {
    let lastY = 0;
    let raf = 0;
    const onScroll = (e: Event) => {
      const target = e.target as HTMLElement | Document;
      const y =
        target === document || target === document.documentElement
          ? window.scrollY
          : (target as HTMLElement).scrollTop ?? 0;

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (y < 20) {
          setProgress(0);
          lastY = y;
          return;
        }
        const delta = y - lastY;
        if (Math.abs(delta) < threshold) return;
        setProgress(delta > 0 ? 1 : 0);
        lastY = y;
      });
    };

    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(raf);
    };
  }, [threshold]);

  return progress;
}

/**
 * Clases "glass" reutilizables para escritorio. Alineadas con Sidebar
 * (empresa) y SidebarWorker: en modo oscuro usan negro puro
 * (dark:bg-black/90) en vez de gris azulado, para que los tres sidebars
 * (empresa / trabajador / cliente) se vean con el mismo tono de fondo.
 */
const GLASS_SURFACE =
  'bg-white/80 dark:bg-black/90 ' +
  'supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-black/70 ' +
  'supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150';

/**
 * Liquid Glass real, estilo iOS 26 / WhatsApp iOS / InfoJobs.
 * Múltiples capas: tinte, blur+saturate, borde de cristal, sombra difusa,
 * highlight superior (before) y sheen diagonal (after).
 */
const LIQUID_GLASS_BAR =
  'isolate overflow-hidden ' +
  'bg-white/85 dark:bg-[#1c1c1e]/90 ' +
  'supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-[#1c1c1e]/70 ' +
  'supports-[backdrop-filter]:backdrop-blur-[38px] supports-[backdrop-filter]:backdrop-saturate-[180%] ' +
  'border border-black/[0.08] dark:border-white/[0.12] ' +
  'shadow-[0_8px_32px_rgba(16,50,84,0.16),0_2px_8px_rgba(16,50,84,0.10),inset_0_1px_0_rgba(255,255,255,0.5),inset_0_-1px_0_rgba(255,255,255,0.06)] ' +
  'dark:shadow-[0_8px_32px_rgba(0,0,0,0.55),0_2px_8px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.3)] ' +
  'before:content-[""] before:absolute before:inset-0 before:pointer-events-none before:rounded-[inherit] ' +
  'before:bg-[radial-gradient(120%_60%_at_50%_0%,rgba(255,255,255,0.55),rgba(255,255,255,0)_60%)] ' +
  'dark:before:bg-[radial-gradient(120%_60%_at_50%_0%,rgba(255,255,255,0.16),rgba(255,255,255,0)_60%)] ' +
  'after:content-[""] after:absolute after:inset-0 after:pointer-events-none after:rounded-[inherit] ' +
  'after:bg-[linear-gradient(115deg,rgba(255,255,255,0.35)_0%,rgba(255,255,255,0)_28%,rgba(255,255,255,0)_72%,rgba(255,255,255,0.18)_100%)] ' +
  'dark:after:bg-[linear-gradient(115deg,rgba(255,255,255,0.10)_0%,rgba(255,255,255,0)_28%,rgba(255,255,255,0)_72%,rgba(255,255,255,0.05)_100%)]';

/**
 * Cápsula de cristal activa: gota de vidrio con blur, brillo, borde y
 * reflejo, sobresaliendo levemente del fondo.
 */
const GLASS_PILL =
  'absolute inset-0 -m-1.5 rounded-[22px] pointer-events-none isolate overflow-hidden ' +
  'bg-white/[0.55] dark:bg-white/[0.10] ' +
  'supports-[backdrop-filter]:backdrop-blur-md supports-[backdrop-filter]:backdrop-saturate-150 ' +
  'border border-white/[0.65] dark:border-white/[0.16] ' +
  'shadow-[0_4px_14px_rgba(16,50,84,0.20),0_1px_3px_rgba(16,50,84,0.12),inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_2px_rgba(16,50,84,0.10)] ' +
  'dark:shadow-[0_4px_14px_rgba(0,0,0,0.5),0_1px_3px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_2px_rgba(0,0,0,0.35)] ' +
  'before:content-[""] before:absolute before:-inset-1 before:rounded-[24px] ' +
  'before:bg-[radial-gradient(80%_70%_at_50%_0%,rgba(255,255,255,0.9),rgba(255,255,255,0)_65%)] ' +
  'dark:before:bg-[radial-gradient(80%_70%_at_50%_0%,rgba(255,255,255,0.28),rgba(255,255,255,0)_65%)]';

const SidebarClient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const scrollProgress = useScrollProgress(currentPath);

  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const navItems = useMemo(
    () => [
      { to: '/panel-cliente/dashboard', icon: Home, label: 'Inicio', end: false },
      { to: '/panel-cliente/profile', icon: UsersIcon, label: 'Mi perfil', end: false },
      { to: '/panel-cliente/explore', icon: Search, label: 'Explorar', end: false },
      { to: '/panel-cliente/appointments', icon: Calendar, label: 'Historial', end: false },
      { to: '/panel-cliente/bonus', icon: Tickets, label: 'Mis bonos', end: false },
    ],
    []
  );

  const currentTabIndex = useMemo(() => {
    const idx = navItems.findIndex(
      (tb) =>
        tb.to && (tb.end ? currentPath === tb.to : currentPath.startsWith(tb.to))
    );
    return idx === -1 ? null : idx;
  }, [navItems, currentPath]);

  const highlightIndex = isDragging ? dragIndex : currentTabIndex;

  const getIndexFromX = (x: number) => {
    let closest = 0;
    let closestDist = Infinity;
    tabRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const dist = Math.abs(x - center);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    return closest;
  };

  const spawnRipple = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> | React.PointerEvent<HTMLElement>
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const point = 'touches' in e ? e.touches[0] : e;
    if (!point) return;
    const x = point.clientX - rect.left;
    const y = point.clientY - rect.top;
    const id = Date.now() + Math.random();
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 650);
  };

  const handleTabPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    spawnRipple(e);
    try {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // Ignorar si el navegador no soporta capture
    }
    setIsDragging(true);
    setDragIndex(getIndexFromX(e.clientX));
  };

  const handleTabPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!isDragging) return;
    setDragIndex(getIndexFromX(e.clientX));
  };

  const handleTabPointerUp = () => {
    if (dragIndex !== null) {
      const tab = navItems[dragIndex];
      if (tab.to && !guardNavigate(tab.to)) {
        navigate(tab.to);
      }
    }
    setIsDragging(false);
    setDragIndex(null);
  };

  const tabbarPadding = 0.25 - 0.1 * scrollProgress;
  const tabbarBottomGap = 0.6 - 0.12 * scrollProgress;
  const tabbarSideMargin = 1 + 0.5 * scrollProgress; // rem — crece al hacer scroll, estrechando la barra

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:min-h-screen w-72 ${GLASS_SURFACE} border-r border-white/40 dark:border-white/10 shadow-2xl shrink-0`}
      >
        <motion.div
          className="flex flex-col h-full p-4 overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3 p-3 mb-4 rounded-2xl cursor-pointer"
            onClick={() => navigate('/panel-cliente')}
          >
            <div className="w-10 h-10 bg-[#103254] text-white rounded-full flex items-center justify-center text-lg font-bold shrink-0">
              C
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-gray-900 dark:text-white truncate text-sm">
                Panel cliente
              </span>
              <span className="text-gray-500 dark:text-gray-400 text-xs">
                Bookoll
              </span>
            </div>
          </motion.div>

          <div className="mx-3 mb-4 border-b border-gray-200/70 dark:border-white/10" />

          <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
            {navItems.map((item) => (
              <motion.div key={item.to} variants={itemVariants}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 text-sm rounded-xl transition ${
                      isActive
                        ? 'bg-[#103254] text-white shadow-lg shadow-[#103254]/30'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-white/5'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              </motion.div>
            ))}
          </nav>
        </motion.div>
      </aside>

      {/* ── MOBILE: TAB BAR INFERIOR LIQUID GLASS (iOS 26 style) ── */}
      {/* Portal a document.body: evita que un ancestro con transform/filter
          (p. ej. wrappers de transición de página con Framer Motion) rompa
          el position:fixed y desplace la barra fuera del borde inferior. */}
      {createPortal(
      <nav
        onPointerDown={handleTabPointerDown}
        onPointerMove={handleTabPointerMove}
        onPointerUp={handleTabPointerUp}
        onPointerCancel={handleTabPointerUp}
        className={`fixed z-50 lg:hidden flex items-center justify-around rounded-[34px] transition-[padding,bottom,left,right] duration-300 ease-out select-none ${LIQUID_GLASS_BAR}`}
        style={{
          left: `${tabbarSideMargin}rem`,
          right: `${tabbarSideMargin}rem`,
          bottom: `calc(${tabbarBottomGap}rem + env(safe-area-inset-bottom))`,
          paddingTop: `${tabbarPadding}rem`,
          paddingBottom: `${tabbarPadding}rem`,
          paddingLeft: '0.4rem',
          paddingRight: '0.4rem',
          touchAction: 'none',
        }}
      >
        {/* Ondas de agua al pulsar */}
        <AnimatePresence>
          {ripples.map((r) => (
            <motion.span
              key={r.id}
              className="absolute rounded-full pointer-events-none z-0"
              style={{
                left: r.x,
                top: r.y,
                translateX: '-50%',
                translateY: '-50%',
                background:
                  'radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0) 70%)',
              }}
              initial={{ width: 0, height: 0, opacity: 0.9 }}
              animate={{ width: 260, height: 260, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.65, ease: 'easeOut' }}
            />
          ))}
        </AnimatePresence>

        {navItems.map((tab, i) => {
          const active = highlightIndex === i;
          const Icon = tab.icon;
          const p = scrollProgress;
          const iconSize = 1.15 - 0.12 * p;

          return (
            <div
              key={tab.label}
              ref={(el) => (tabRefs.current[i] = el)}
              className="relative z-10 flex justify-center items-center flex-1 py-1"
              aria-label={tab.label}
            >
              <motion.div
                className="relative z-10 flex flex-col justify-center items-center gap-0.5 px-2 py-1"
                animate={{ scale: active ? 1.08 : 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.7 }}
              >
                {active && (
                  <motion.span
                    layoutId="tab-indicator-client"
                    className={GLASS_PILL}
                    transition={{ type: 'spring', stiffness: 300, damping: 27, mass: 0.7 }}
                  />
                )}

                <Icon
                  className={`relative z-10 transition-colors duration-200 ${
                    active
                      ? 'text-[#0d1f33] dark:text-white'
                      : 'text-gray-700 dark:text-white/60'
                  }`}
                  style={{ width: `${iconSize}rem`, height: `${iconSize}rem` }}
                  strokeWidth={active ? 2.2 : 1.75}
                />

                <span
                  className={`relative z-10 text-[10px] leading-none font-medium whitespace-nowrap transition-colors duration-200 ${
                    active
                      ? 'text-[#0d1f33] dark:text-white'
                      : 'text-gray-700 dark:text-white/60'
                  }`}
                >
                  {tab.label}
                </span>
              </motion.div>
            </div>
          );
        })}
      </nav>,
      document.body
      )}
    </>
  );
};

export default memo(SidebarClient);